import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  ArrowLeft, Calendar, FileText, Paperclip, User, Activity,
  Printer, ClipboardList, CheckCircle, XCircle, Clock, AlertCircle,
  Download, Filter, X,
} from 'lucide-react';
import { parseLocalDate } from '../utils/timeFormat';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Per-subject chart view. Aggregates every appointment for a pet into a
 * chronological timeline with notes rendered inline + attached documents
 * + any form submissions the owner has completed. One page, one scroll,
 * all the context a staff member needs to walk into a grooming / service
 * session prepared.
 *
 * Routed at /pets/:id/chart. Admin-only in v1 (groomers get a read-only
 * variant automatically via the backend's role-based redaction of
 * admin_notes).
 *
 * Note-rendering note: `groomer_notes` and `admin_notes` columns may
 * contain either free-form strings OR a JSON envelope (when the business
 * notes format is SOAP/custom — see AppointmentNotesEditor for the
 * envelope shape). parseNote() handles both transparently.
 */
export default function PetChart() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  // Filters: empty strings mean "no filter". Date strings are YYYY-MM-DD
  // (HTML date input format) which sorts lexicographically against the
  // backend's appointment_date column — no parsing needed for the
  // comparison.
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterService, setFilterService] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_URL}/pets/${id}/chart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChart(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load chart');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Unique service names from this pet's history, for the service
  // dropdown. Sorted alphabetically; nulls dropped.
  const serviceOptions = useMemo(() => {
    if (!chart) return [];
    const set = new Set();
    for (const a of chart.appointments) {
      if (a.service_name) set.add(a.service_name);
    }
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  }, [chart]);

  const filteredAppointments = useMemo(() => {
    if (!chart) return [];
    return chart.appointments.filter((a) => {
      if (filterService && a.service_name !== filterService) return false;
      const d = a.appointment_date;
      if (filterFrom && d && d < filterFrom) return false;
      if (filterTo   && d && d > filterTo)   return false;
      return true;
    });
  }, [chart, filterFrom, filterTo, filterService]);

  const hasActiveFilters = !!(filterFrom || filterTo || filterService);
  const clearFilters = () => { setFilterFrom(''); setFilterTo(''); setFilterService(''); };

  /**
   * Export the chart as a PDF reflecting the current filters. The
   * endpoint is auth-gated, so we fetch as a Blob and trigger a
   * synthetic anchor click (a plain <a href> can't pass the Bearer
   * token).
   */
  const exportPdf = async () => {
    if (!chart) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterFrom)    params.set('from', filterFrom);
      if (filterTo)      params.set('to', filterTo);
      if (filterService) params.set('service', filterService);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API_URL}/pets/${id}/chart.pdf${qs}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const safeName = (chart.pet?.name || 'pet').replace(/[^A-Za-z0-9_-]+/g, '_');
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chart-${safeName}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (!chart) return null;

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-gray-50 py-6 print:bg-white print:py-0">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg"
            title="Back"
          >
            <ArrowLeft size={24} className="text-gray-600" />
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={exportPdf}
              disabled={exporting}
              className="btn btn-secondary flex items-center space-x-2 disabled:opacity-50"
              title="Export the chart (with current filters) as a PDF"
            >
              <Download size={16} />
              <span>{exporting ? 'Exporting…' : 'Export PDF'}</span>
            </button>
            <button
              onClick={() => window.print()}
              className="btn btn-secondary flex items-center space-x-2"
              title="Print chart"
            >
              <Printer size={16} />
              <span>Print</span>
            </button>
          </div>
        </div>

        {/* Pet summary card */}
        <PetHeader pet={chart.pet} stats={chart.stats} />

        {/* Documents section */}
        {chart.documents.length > 0 && (
          <Section title="Documents" icon={<Paperclip size={18} />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {chart.documents.map((d) => (
                <a
                  key={d.id}
                  href={d.document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 p-3 bg-gray-50 border border-gray-200 rounded hover:border-primary-400 transition-colors"
                >
                  <FileText size={16} className="text-primary-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{d.document_name}</div>
                    {d.document_type && (
                      <div className="text-xs text-gray-500">{d.document_type}</div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* Forms section */}
        {chart.forms.length > 0 && (
          <Section title="Form submissions" icon={<ClipboardList size={18} />}>
            <div className="space-y-1">
              {chart.forms.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-2">
                    <ClipboardList size={14} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-800">{f.form_name}</span>
                    {f.submitter_name && (
                      <span className="text-xs text-gray-500">by {f.submitter_name}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {f.submitted_at ? new Date(f.submitted_at).toLocaleDateString() : '—'}
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Encounter timeline */}
        <Section title="Encounter history" icon={<Activity size={18} />}>
          {/* Filter bar — hidden on print so the printed copy reflects
              what the user filtered to without UI chrome. */}
          {chart.appointments.length > 0 && (
            <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-gray-50 border border-gray-200 rounded print:hidden">
              <div className="flex items-center space-x-1 text-gray-600 text-sm font-medium pb-1">
                <Filter size={14} />
                <span>Filter</span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">From</label>
                <input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="input input-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">To</label>
                <input
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="input input-sm text-sm"
                />
              </div>
              <div className="min-w-[180px]">
                <label className="block text-xs text-gray-500 mb-0.5">Service</label>
                <select
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                  className="input input-sm text-sm w-full"
                >
                  <option value="">All services</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center space-x-1 pb-1"
                  title="Clear all filters"
                >
                  <X size={14} />
                  <span>Clear</span>
                </button>
              )}
              <div className="text-xs text-gray-500 ml-auto pb-1">
                Showing {filteredAppointments.length} of {chart.appointments.length}
              </div>
            </div>
          )}
          {chart.appointments.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No appointments yet. The first booking will appear here.
            </p>
          ) : filteredAppointments.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No appointments match the current filters.
            </p>
          ) : (
            <ol className="space-y-4">
              {filteredAppointments.map((apt) => (
                <EncounterCard key={apt.id} appointment={apt} isAdmin={isAdmin} />
              ))}
            </ol>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─── Layout helpers ──────────────────────────────────────────────────

function Section({ title, icon, children }) {
  return (
    <div className="card mb-4 print:shadow-none print:border print:border-gray-300">
      <h2 className="flex items-center space-x-2 text-gray-900 font-bold mb-3">
        <span className="text-primary-600">{icon}</span>
        <span>{title}</span>
      </h2>
      {children}
    </div>
  );
}

function PetHeader({ pet, stats }) {
  return (
    <div className="card mb-4 print:shadow-none print:border print:border-gray-300">
      <div className="flex items-start space-x-4">
        {pet.image_url ? (
          <img
            src={pet.image_url}
            alt={pet.name}
            className="w-20 h-20 rounded-lg object-cover border border-gray-200"
          />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
            <User size={32} className="text-gray-400" />
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{pet.name}</h1>
          <div className="text-sm text-gray-600 mt-1 space-y-0.5">
            {(pet.breed || pet.size) && (
              <div>
                {[pet.breed, pet.size].filter(Boolean).join(' · ')}
                {pet.age ? ` · ${pet.age} years` : ''}
              </div>
            )}
            {(pet.owner_first_name || pet.owner_last_name) && (
              <div>
                <strong>Owner:</strong> {[pet.owner_first_name, pet.owner_last_name].filter(Boolean).join(' ')}
                {pet.owner_email && <span className="text-gray-500"> · {pet.owner_email}</span>}
                {pet.owner_phone && <span className="text-gray-500"> · {pet.owner_phone}</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-200">
        <Stat label="Total visits" value={stats.total_visits} />
        <Stat label="First visit" value={stats.first_visit ? parseLocalDate(stats.first_visit).toLocaleDateString() : '—'} />
        <Stat label="Last visit"  value={stats.last_visit  ? parseLocalDate(stats.last_visit).toLocaleDateString()  : '—'} />
      </div>

      {(pet.medical_notes || pet.behavioral_notes || pet.special_notes) && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-sm">
          {pet.medical_notes && (
            <div><strong className="text-red-700">Medical:</strong> <span className="text-gray-800">{pet.medical_notes}</span></div>
          )}
          {pet.behavioral_notes && (
            <div><strong className="text-amber-700">Behavioural:</strong> <span className="text-gray-800">{pet.behavioral_notes}</span></div>
          )}
          {pet.special_notes && (
            <div><strong className="text-gray-700">General:</strong> <span className="text-gray-800">{pet.special_notes}</span></div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-base font-bold text-gray-900 mt-0.5">{value}</div>
    </div>
  );
}

// ─── Encounter (per-appointment) card ────────────────────────────────

function EncounterCard({ appointment, isAdmin }) {
  const groomerName = [appointment.groomer_first_name, appointment.groomer_last_name]
    .filter(Boolean).join(' ') || '—';

  const groomerNote = parseNote(appointment.groomer_notes);
  const adminNote   = isAdmin ? parseNote(appointment.admin_notes) : null;

  return (
    <li className="border-l-4 border-primary-500 pl-4 py-2">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center space-x-2">
            <Calendar size={14} className="text-gray-400" />
            <span className="font-semibold text-gray-900">
              {parseLocalDate(appointment.appointment_date).toLocaleDateString()}
              {' '}
              <span className="text-sm font-normal text-gray-500">{appointment.start_time}</span>
            </span>
            <StatusBadge status={appointment.status} />
          </div>
          <div className="text-sm text-gray-700 mt-0.5">
            <strong>{appointment.service_name || 'Appointment'}</strong>
            {' · '}with {groomerName}
          </div>
        </div>
      </div>

      {appointment.special_requests && (
        <div className="mt-2 text-sm bg-blue-50 border border-blue-200 rounded p-2">
          <strong className="text-blue-900">Client requested:</strong>{' '}
          <span className="text-gray-800">{appointment.special_requests}</span>
        </div>
      )}

      {appointment.cancellation_reason && (
        <div className="mt-2 text-sm bg-red-50 border border-red-200 rounded p-2">
          <strong className="text-red-900">Cancelled:</strong>{' '}
          <span className="text-gray-800">{appointment.cancellation_reason}</span>
        </div>
      )}

      <NoteBlock title="Provider notes" note={groomerNote} tone="staff" />
      {isAdmin && <NoteBlock title="Admin-only notes" note={adminNote} tone="admin" />}

      {Array.isArray(appointment.attachments) && appointment.attachments.length > 0 && (
        <AttachmentStrip attachments={appointment.attachments} />
      )}
    </li>
  );
}

/**
 * Read-only thumbnail strip for appointment attachments rendered on the
 * chart. Images get 80x80 preview tiles that open full-size in a new tab;
 * PDFs render as a file-icon tile with the filename. Upload / delete
 * happens in AppointmentAttachmentsModal — the chart is an archival view.
 */
function AttachmentStrip({ attachments }) {
  return (
    <div className="mt-2">
      <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
        Attachments ({attachments.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {attachments.map((att) => {
          const isImage = typeof att.mime_type === 'string' && att.mime_type.startsWith('image/');
          return (
            <a
              key={att.id}
              href={att.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-20 border border-gray-200 rounded overflow-hidden bg-white hover:border-primary-400 transition-colors print:break-inside-avoid"
              title={att.label || att.filename}
            >
              {isImage ? (
                <img
                  src={att.file_url}
                  alt={att.label || att.filename}
                  className="w-20 h-20 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-20 h-20 bg-gray-50 flex flex-col items-center justify-center p-1">
                  <FileText size={24} className="text-primary-600" />
                  <span className="text-[10px] text-gray-600 truncate max-w-full mt-1">
                    {att.filename}
                  </span>
                </div>
              )}
              {att.label && (
                <div className="px-1 py-0.5 text-[10px] text-gray-700 truncate">{att.label}</div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: { label: 'Completed', cls: 'bg-green-100 text-green-800',  icon: <CheckCircle size={12} /> },
    confirmed: { label: 'Confirmed', cls: 'bg-blue-100 text-blue-800',    icon: <CheckCircle size={12} /> },
    pending:   { label: 'Pending',   cls: 'bg-yellow-100 text-yellow-800', icon: <Clock size={12} /> },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-200 text-gray-700',    icon: <XCircle size={12} /> },
    no_show:   { label: 'No-show',   cls: 'bg-orange-100 text-orange-800', icon: <AlertCircle size={12} /> },
  };
  const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700', icon: null };
  return (
    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon}
      <span>{s.label}</span>
    </span>
  );
}

function NoteBlock({ title, note, tone }) {
  if (!note || (!note.raw && Object.keys(note.structured || {}).length === 0)) return null;
  const headerClass = tone === 'admin'
    ? 'text-red-700 font-semibold text-xs uppercase tracking-wide'
    : 'text-gray-700 font-semibold text-xs uppercase tracking-wide';
  const bodyClass = tone === 'admin'
    ? 'bg-red-50 border border-red-200 rounded p-3'
    : 'bg-gray-50 border border-gray-200 rounded p-3';

  return (
    <div className="mt-2">
      <div className={headerClass + ' mb-1'}>{title}</div>
      <div className={bodyClass}>
        {/* Structured (SOAP / custom) renders as labelled blocks. */}
        {note.labels.map((label) => (
          note.structured[label] ? (
            <div key={label} className="mb-2 last:mb-0">
              <div className="text-xs font-bold text-gray-700">{label}</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap">{note.structured[label]}</div>
            </div>
          ) : null
        ))}

        {/* Raw / legacy content renders as plain text below structured. */}
        {note.raw && (
          <div className={note.labels.length > 0 ? 'mt-2 pt-2 border-t border-gray-200 text-sm text-gray-800 whitespace-pre-wrap' : 'text-sm text-gray-800 whitespace-pre-wrap'}>
            {note.raw}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Parse a stored note string. Mirrors the logic in AppointmentNotesEditor
 * — if the string is a structured envelope we return `{labels, structured,
 * raw}` where raw holds any orphan legacy text; otherwise we return the
 * whole string in `raw` with empty structured. Never throws.
 */
function parseNote(stored) {
  if (!stored || typeof stored !== 'string') return null;
  const trimmed = stored.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.values === 'object' && Array.isArray(parsed.labels)) {
        const structured = {};
        for (const lbl of parsed.labels) {
          if (typeof parsed.values[lbl] === 'string' && parsed.values[lbl].length > 0) {
            structured[lbl] = parsed.values[lbl];
          }
        }
        const legacy = typeof parsed.values._legacy === 'string' ? parsed.values._legacy : '';
        return { labels: parsed.labels, structured, raw: legacy };
      }
    } catch (_) { /* fall through */ }
  }
  return { labels: [], structured: {}, raw: stored };
}
